// Copyright (c) 2023 WiseTime. All rights reserved.

import { CreateEmptyTimeGroupRequest, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { concat, EMPTY, Observable, of } from "rxjs"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { showSnackBar } from "../../snackbar/actions"
import { t } from "@lingui/macro"
import * as TimelineActions from "../actions"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as TimelineSlice from "../slices/timeline"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"

type CreateEmptyTimeGroupRpc = (request: CreateEmptyTimeGroupRequest) => Observable<TimeGroup>

export const createEmptyTimeGroup = (createEmptyTimeGroupRpc: CreateEmptyTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.createEmptyTimeGroup.match),
      mergeMap(({ payload }) =>
        of(payload.request)
          .pipe(
            mergeMap(createEmptyTimeGroupRpc),
            mergeMap(timeGroup => {
              payload.onSuccess && payload.onSuccess()
              const optionalRemoveFromCreateRowIndexesList = payload.createTimeGroupRowIndex !== undefined ?
                of(TimelineSlice.removeFromCreateRowIndexesList(payload.createTimeGroupRowIndex)) : EMPTY
              return concat(
                optionalRemoveFromCreateRowIndexesList,
                of(TimelineSlice.upsertTimeGroup(timeGroup.toObject())),
                of(TimelineSlice.replaceActiveTimeGroupsIds({
                  timeGroupsIds: payload.skipSelection ? [] : [timeGroup.getId()],
                })),
              )
            }),
            catchError((e) => handleError(e, payload)),
          ),
      )
    )

const handleError = (e: unknown, payload: TimelineActions.CreateEmptyTimeGroupPayload) => {
  payload.onError && payload.onError()
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_create_empty_group_permission_denied", message: "Failed to create time group. You do not have permission to perform this action." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_create_time_group_failed", message: "Failed to create the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const createEmptyTimeGroupRpc = (request: CreateEmptyTimeGroupRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.createEmptyTimeGroup(request, null))

export default createEmptyTimeGroup(createEmptyTimeGroupRpc)
